import { SENSOR_CHANNELS, lookupSensorChannel } from './sensorRegistry.generated';
import { SENSOR_IMAGES, SENSOR_GROUPS, imageForGroupKey } from './sensorConfig';

export const friendlyNameConverter = (sensorKey) => {
  const sensorSplit = sensorKey.split(':');
  if (sensorSplit.length !== 2) return getSensorType(sensorSplit.at(-1));
  const sensorType = sensorSplit[1];
  const sensorName = sensorSplit[0] === 'null' ? '' : sensorSplit[0];
  return `${sensorName} ${getSensorType(sensorType)}`;
};

/**
 * Short display name — what charts, legends and rule descriptions show.
 *
 * NOTE: callers compare this value for equality to decide whether two data
 * keys are "the same sensor" (ruleEngine.js, ExceedanceWidget.js,
 * DataCapturePage.js). Prefer getSensorGroupKey() for that; a generator check
 * keeps the two partitions identical so the existing comparisons stay correct.
 */
export const getSensorType = (sensorType) => lookupSensorChannel(sensorType)?.shortName ?? sensorType;

/** Long display name — export columns, tooltips, detail views. */
export const getSensorLongName = (sensorType) => lookupSensorChannel(sensorType)?.longName ?? sensorType;

/** Unit for a channel, or null when it has none (GPS, RNG, sensor id). */
export const getSensorUnit = (sensorType) => lookupSensorChannel(sensorType)?.unit ?? null;

/** Stable measurement identity — use this to compare two data keys. */
export const getSensorGroupKey = (sensorType) => lookupSensorChannel(sensorType)?.groupKey ?? sensorType;

export const getSensorTypeImage = (sensorType) => {
  const channel = lookupSensorChannel(sensorType);
  if (channel) return imageForGroupKey(channel.groupKey);
  // Callers also pass an already-resolved display name.
  const byName = SENSOR_CHANNELS.find((c) => c.shortName.toLowerCase() === String(sensorType).toLowerCase());
  return byName ? imageForGroupKey(byName.groupKey) : SENSOR_IMAGES.DEFAULT;
};

export const getSensorObject = (sensorType) => {
  if (!sensorType || typeof sensorType !== 'string') {
    throw new Error('sensorType must be a non-empty string');
  }

  const channel = lookupSensorChannel(sensorType);
  if (!channel) {
    return { matchingIds: [sensorType], name: sensorType, imageLocation: SENSOR_IMAGES.DEFAULT };
  }

  return {
    matchingIds: SENSOR_GROUPS[channel.shortName] || [sensorType],
    name: channel.shortName,
    imageLocation: imageForGroupKey(channel.groupKey),
  };
};

/** Every lookup token the registry recognises. */
export const getAllSensorTypes = () =>
  SENSOR_CHANNELS.flatMap((channel) => [channel.key, ...channel.aliases]).map((token) => token.toLowerCase());

/** Every distinct display name. */
export const getAllSensorGroups = () => [...new Set(SENSOR_CHANNELS.map((channel) => channel.shortName))];

export const isValidSensorType = (sensorType) =>
  typeof sensorType === 'string' && sensorType.length > 0 && Boolean(lookupSensorChannel(sensorType));
